import React, { useState, useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { format, subDays } from 'date-fns'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined'
import BoltIcon from '@mui/icons-material/Bolt'
import KPICard from '../components/KPICard'
import InterventionFilters from '../components/InterventionFilters'
import TrendChart from '../components/TrendChart'
import ChannelBreakdownChart from '../components/ChannelBreakdownChart'
import FRTDisplay from '../components/FRTDisplay'
import MerchantBreakdownTable from '../components/MerchantBreakdownTable'
import { CHANNEL_COLORS } from '../components/channelColors'
import { MOCK_DASHBOARD } from '../mockData'
import { useGetDashboardSummaryQuery } from '../../../redux/interventionApiSlice'

const USE_MOCK = false // set true to use mock data

export default function InterventionDashboard() {
  const [filters, setFilters] = useState({
    type: 'BOTH',
    merchantOids: [],
    dateRange: {
      from: format(subDays(new Date(), 2), 'yyyy-MM-dd'),
      to: format(new Date(), 'yyyy-MM-dd'),
    },
  })

  const noMerchant = !USE_MOCK && !filters.merchantOids?.length
  const { data: apiData, isFetching } = useGetDashboardSummaryQuery(filters, { skip: USE_MOCK || noMerchant })
  const data = USE_MOCK ? MOCK_DASHBOARD : apiData
  const isLoading = USE_MOCK ? false : isFetching

  const s = data?.summary || {}
  const p = data?.previous || {}
  const cb = data?.channelBreakdown || {}

  const channelDonut = useMemo(() => [
    { label: 'WhatsApp', value: cb.whatsapp?.sent || 0, color: CHANNEL_COLORS.whatsapp },
    { label: 'Calls (Futwork)', value: cb.calls_futwork?.attempted || 0, color: CHANNEL_COLORS.calls_futwork },
    { label: 'Automated Rules', value: cb.automated_rule?.triggered || 0, color: CHANNEL_COLORS.automated_rule },
    { label: 'Manual', value: cb.manual?.actions || 0, color: CHANNEL_COLORS.manual },
  ].filter(d => d.value > 0), [cb])

  const merchantCols = [
    { key: 'merchantName', label: 'Merchant', format: 'text' },
    { key: 'totalNdr', label: 'NDRs', format: 'number', align: 'right' },
    { key: 'interventions', label: 'Interventions', format: 'number', align: 'right' },
    { key: 'deliveryRate', label: 'Delivery %', format: 'percent', align: 'right' },
    { key: 'frtAvg', label: 'Avg FRT (min)', format: 'number', align: 'right' },
    { key: 'costPerDelivered', label: 'Cost/Delivered', format: 'currency', align: 'right' },
  ]

  return (
    <Box>
      <Typography variant='h5' sx={{ mb: 2 }}>Intervention Analytics Dashboard</Typography>

      <InterventionFilters filters={filters} onChange={setFilters} />

      {noMerchant ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant='body1' sx={{ color: 'text.disabled' }}>Select a merchant to view data</Typography>
        </Box>
      ) : <>

      {/* KPI Row */}
      <Box className='kpi-row'>
        <KPICard title='Total NDR Raised' value={s.totalNdrRaised} previousValue={p.totalNdrRaised} icon={<AssessmentOutlinedIcon />} color='#0F4C81' loading={isLoading} />
        <KPICard title='Intervention Rate' value={s.interventionRate} previousValue={p.interventionRate} icon={<TrendingUpIcon />} color='#2E7D32' format='percent' loading={isLoading} />
        <KPICard title='Avg FRT' value={s.avgFRT_minutes} icon={<AccessTimeIcon />} color='#ED6C02' format='minutes' loading={isLoading} />
        <KPICard title='Delivery Success' value={s.deliverySuccessRate} previousValue={p.deliverySuccessRate} icon={<CheckCircleOutlineIcon />} color='#2E7D32' format='percent' loading={isLoading} />
        <KPICard title='Cost / Delivered' value={s.costPerDelivered} icon={<PaidOutlinedIcon />} color='#E85D3A' format='currency' loading={isLoading} />
        <KPICard title='Total Interventions' value={s.totalInterventions} previousValue={p.totalInterventions} icon={<BoltIcon />} color='#8B5CF6' loading={isLoading} />
      </Box>

      {/* Trend Chart */}
      <TrendChart
        data={data?.trend || []}
        title='Daily Trend — NDR Raised vs Interventions vs Delivered'
        series={[
          { key: 'ndrRaised', label: 'NDR Raised', color: '#0F4C81' },
          { key: 'interventions', label: 'Interventions', color: '#8B5CF6' },
          { key: 'delivered', label: 'Delivered', color: '#2E7D32' },
        ]}
        loading={isLoading}
      />

      {/* Channel Breakdown + FRT */}
      <Box className='chart-grid-2' sx={{ mt: 2 }}>
        <ChannelBreakdownChart data={channelDonut} title='Interventions by Channel' loading={isLoading} />
        <FRTDisplay avg={s.avgFRT_minutes} p50={s.frtP50_minutes} p90={s.frtP90_minutes} loading={isLoading} />
      </Box>

      {/* Merchant Breakdown */}
      <MerchantBreakdownTable
        data={data?.merchantBreakdown || []}
        columns={merchantCols}
        title='Merchant-Level Breakdown'
        loading={isLoading}
        defaultSort={{ field: 'totalNdr', direction: 'desc' }}
      />
      </>}
    </Box>
  )
}
